import React, { useState, useEffect } from 'react';
import config from '../../config/config';

export default function InsertAnimalStatus() {
  const [searchForm, setSearchForm] = useState({
    animal_type: '',
    category: '',
    day: ''
  });
  const [animals, setAnimals] = useState([]);
  const [message, setMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [types] = useState(['cow', 'goat', 'sheep', 'camel']);
  const [days] = useState(['1', '2', '3']);

  useEffect(() => {
    // Fetch categories from backend
    const fetchMetadata = async () => {
      try {
        const response = await fetch(`${config.baseUrl}/api/animals/metadata`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setCategories(data.categories);
        }
      } catch (error) {
        console.error('Error fetching metadata:', error);
        setMessage('Error fetching categories');
      }
    };

    fetchMetadata();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${config.baseUrl}/api/animals/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(searchForm)
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data || [];
        setAnimals(data);
        setMessage(`Found ${data.length} animals matching your criteria`);
      } else {
        setMessage('Failed to fetch animals');
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage('Error searching for animals');
    }
  };

  const getCategoryName = (code) => {
    const category = categories.find(cat => cat.code === code);
    return category ? category.name : code;
  };

  const updateStatus = async (animalId, status) => {
    try {
      // Get the current animal's details
      const animal = animals.find(a => a.id === animalId);
      if (!animal) return;

      const response = await fetch(`${config.baseUrl}/api/animals/${animalId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status,
          animal_type: animal.animal_type,
          category: animal.category,
          day: animal.day
        })
      });

      if (response.ok) {
        const updatedData = await response.json();

        // Update only matching animals in the state
        setAnimals(animals.map(animal => {
          const shouldUpdate = updatedData.some(updated =>
            updated.id === animal.id ||
            (updated.animal_number === animal.animal_number &&
              updated.animal_type === animal.animal_type &&
              updated.category === animal.category &&
              updated.day === animal.day)
          );

          if (shouldUpdate) {
            const updatedAnimal = updatedData.find(u =>
              u.animal_number === animal.animal_number &&
              u.animal_type === animal.animal_type &&
              u.category === animal.category &&
              u.day === animal.day
            );
            return updatedAnimal || animal;
          }
          return animal;
        }));

        setMessage(`Status updated to ${status}`);
      } else {
        setMessage('Failed to update status');
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage('Error updating status');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 bg-gray-50">
      <h1 className="text-3xl font-bold mb-6 text-gray-800 border-b pb-4">Update Animal Status</h1>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="mb-8 bg-white p-6 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={searchForm.animal_type}
              onChange={(e) => setSearchForm({ ...searchForm, animal_type: e.target.value })}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="">Select Type</option>
              {types.map(type => (
                <option key={type} value={type} className="capitalize">{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={searchForm.category}
              onChange={(e) => setSearchForm({ ...searchForm, category: e.target.value })}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="">Select Category</option>
              {categories.map(cat => (
                <option key={cat.code} value={cat.code}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Day</label>
            <select
              value={searchForm.day}
              onChange={(e) => setSearchForm({ ...searchForm, day: e.target.value })}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="">Select Day</option>
              {days.map(day => (
                <option key={day} value={day}>Day {day}</option>
              ))}
            </select>
          </div>
        </div>
        <button type="submit" className="mt-6 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
          Search
        </button>
      </form>

      {message && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg">
          {message}
        </div>
      )}

      {/* Animals List */}
      {animals.length > 0 && (
        <div className="overflow-x-auto bg-white rounded-lg shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Animal ID</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Category</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Animal Number</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Day</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {animals.map((animal) => (
                <tr key={animal.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-900">{animal.id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 capitalize">{animal.animal_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{getCategoryName(animal.category)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{animal.animal_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{animal.day}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex gap-4">
                      {['slaughtered', 'in_process', 'ready_to_dispatch', 'delivered'].map(status => (
                        <label key={status} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={animal.status === status}
                            onChange={() => updateStatus(animal.id, status)}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 capitalize">{status.replace(/_/g, ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}